import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ElMessage } from 'element-plus';
import {
  confirmTemplateGeneration,
  consumeTemplateGenerationMessageStream,
  consumeTemplateGenerationStartStream,
  getTemplateGeneration,
} from '@/api/documentWorkbenchApi';
import { formatApiErrorMessage } from '@/api/http';
import { useAiAssistantStore } from '@/stores/useAiAssistantStore';
import { useSmartDocumentAiStore } from '@/stores/useSmartDocumentAiStore';
import type {
  SmartDocumentTemplateGenerateStructured,
  TemplateGenerationProcessStep,
  TemplateGenerationResult,
} from '@/types/documentWorkbench';
import {
  isSmartDocumentScenario,
  resolveSmartDocumentScenario,
} from '@/utils/aiSessionContext';
import { thoughtStepsToJson, type ThoughtStep } from '@/utils/aiThought';
import {
  buildTemplateGenerateUnderstandingMessage,
  mapTemplateGenerateProgressToThought,
  templateGenerateThoughtInitial,
  type TemplateGenerateProgressStep,
} from '@/utils/templateGenerateAiSteps';
import { formatTemplateGenerationError, isTemplateGenerationNetworkError } from '@/utils/templateGenerateErrorMessage';

function isAbortError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const any = e as { code?: string; name?: string; message?: string };
  return any.code === 'ERR_CANCELED'
    || any.name === 'CanceledError'
    || any.name === 'AbortError'
    || /cancel|abort/i.test(String(any.message || ''));
}

/** 打开文档后追加一句预览提示，不覆盖交付摘要 */
function appendOpenedHint(summary: string, title?: string | null): string {
  const base = (summary || '').trim();
  const hint = title
    ? `已在右侧打开预览《${String(title).trim()}》。`
    : '已在右侧打开预览。';
  if (!base) return hint;
  if (/已在右侧打开预览/.test(base)) return base;
  // 去掉打开过程中临时追加的行
  const cleaned = base.replace(/\n\n正在打开预览…\s*$/u, '').trim();
  return `${cleaned}\n\n${hint}`;
}

async function pollTemplateGeneration(
  projectId: string,
  generationId: string,
  signal: AbortSignal,
): Promise<TemplateGenerationResult | null> {
  const deadline = Date.now() + 50 * 60 * 1000;
  while (Date.now() < deadline) {
    if (signal.aborted) return null;
    try {
      const result = await getTemplateGeneration(projectId, generationId);
      if (result.status === 'completed' || result.status === 'failed' || result.status === 'collecting') {
        return result;
      }
    } catch {
      /* 会话尚未可见或网关抖动时继续轮询 */
    }
    await new Promise((resolve) => setTimeout(resolve, 4000));
  }
  return null;
}

const SLOT_LABELS: Record<string, string> = {
  projectName: '项目名称',
  companyName: '企业名称',
  projectId: '项目ID',
  date: '日期',
  datetime: '日期时间',
  personName: '编制人',
  caseDescription: '案例描述',
  noiseRequirement: '整机噪声要求',
  fanNoise: '单风扇噪声限值',
  compressorNoise: '单压缩机噪声限值',
  heatingCapacity: '空载加热能力要求',
  coolingCapacity: '空载制冷能力要求',
  switchMode: '开关模式要求',
  coolingTempSetting: '制冷设计温度档位',
  heatingTempSetting: '加热设计温度档位',
  usageTempRange: '使用环境温度范围',
  workingTempRange: '工作环境温度范围',
  leakageRate: '泄漏量要求',
  reportVersion: '版本编号规则',
  revisionContent: '修订内容',
  confidentialityLevel: '密级',
  electricalEngineerDate: '电气工程师日期',
  bom: '项目 BOM / 物料清单',
  requirements: '需求台账 / SOR',
  tasks: '项目任务 / 里程碑',
  tr_verdict: '评审过程与结论',
  issues: '项目问题清单',
};

export function displayTemplateSlotLabel(key: string): string {
  return SLOT_LABELS[key] || key;
}

export const useSmartDocumentTemplateGenerateStore = defineStore('smartDocumentTemplateGenerate', () => {
  const active = ref(false);
  const sending = ref(false);
  const generationId = ref('');
  const status = ref<string>('');
  const templateId = ref('');
  const templateTitle = ref('');
  const missingFields = ref<string[]>([]);
  const filledSlots = ref<Record<string, string>>({});
  const documentId = ref('');
  const documentTitle = ref('');
  const pendingMessageId = ref<string | null>(null);
  const abortController = ref<AbortController | null>(null);

  /** 工作台生成中实时预览 */
  const livePreviewActive = ref(false);
  const livePreviewHtml = ref('');
  const livePreviewTitle = ref('');
  const livePhaseMessage = ref('');
  const liveWrittenCount = ref(0);
  const liveTotal = ref(0);
  const liveProcessSteps = ref<TemplateGenerationProcessStep[]>([]);
  /** 左侧目录占位项 id（generating:…） */
  const liveListItemId = ref('');
  /** 生成过程中用户切到其他文档查看 */
  const userSwitchedDuringGenerate = ref(false);
  /** 生成完成但未自动打开时，供工作台刷新列表并显示红点 */
  const deferredDocumentId = ref<string | null>(null);

  const boundScenario = ref('');
  const boundSessionId = ref('');

  const aiStore = useAiAssistantStore();
  const smartDocAi = useSmartDocumentAiStore();

  const isCollecting = computed(() => active.value && (status.value === 'collecting' || status.value === 'failed'));
  /** 收集中、失败重试，或已生成但仍有待补充字段时可继续多轮完善 */
  const canContinue = computed(() =>
    active.value
    && !!generationId.value
    && (
      ['collecting', 'failed'].includes(status.value)
      || (status.value === 'completed' && (missingFields.value?.length || 0) > 0)
    ),
  );

  function bindPanelContext(ctx: { scenario: string; sessionId: string }) {
    boundScenario.value = (ctx.scenario || '').trim();
    boundSessionId.value = (ctx.sessionId || '').trim();
  }

  function resolveScenario(): string {
    if (boundScenario.value && isSmartDocumentScenario(boundScenario.value)) {
      return boundScenario.value;
    }
    const scenario = resolveSmartDocumentScenario(smartDocAi.workspaceId || '0');
    if (!isSmartDocumentScenario(aiStore.currentScenario) || aiStore.currentScenario !== scenario) {
      aiStore.currentScenario = scenario;
    }
    return scenario;
  }

  function addMessage(
    role: 'user' | 'assistant',
    content: string,
    structured?: Record<string, unknown> | null,
    thought?: string,
  ) {
    const scenario = resolveScenario();
    const sessionId = boundSessionId.value || undefined;
    if (sessionId) {
      return aiStore.addMessage(scenario, sessionId, role, content, undefined, thought, structured);
    }
    return aiStore.addMessage(scenario, role, content, undefined, thought, structured);
  }

  function buildStructured(
    result: Partial<TemplateGenerationResult> & { deliverySummary?: string },
    overrideStatus?: string,
  ): SmartDocumentTemplateGenerateStructured {
    return {
      kind: 'smart_document_template_generate',
      status: overrideStatus || result.status || status.value || 'collecting',
      generationId: result.generationId || generationId.value || undefined,
      templateId: result.templateId || templateId.value || undefined,
      templateTitle: result.templateTitle || templateTitle.value || undefined,
      missingFields: result.missingFields ?? missingFields.value,
      filledSlots: result.filledSlots ?? filledSlots.value,
      documentId: result.documentId || documentId.value || undefined,
      documentTitle: result.documentTitle || documentTitle.value || undefined,
      errorMessage: result.errorMessage,
      matchHits: result.matchHits,
      referenceSummaries: result.referenceSummaries,
      usedSourceFormat: result.usedSourceFormat,
      processSteps: result.processSteps,
      intentSummary: result.intentSummary,
      fillInstructions: result.fillInstructions,
      outlineSummary: result.outlineSummary,
      validationIssueCount: result.validationIssueCount,
      pendingConfirmCount: result.pendingConfirmCount,
      qualitySummary: result.qualitySummary,
      reviewChecklist: result.reviewChecklist,
      deliverySummary: result.deliverySummary,
    };
  }

  function applyResult(result: TemplateGenerationResult) {
    generationId.value = result.generationId || generationId.value;
    status.value = result.status || status.value;
    templateId.value = result.templateId || templateId.value;
    templateTitle.value = result.templateTitle || templateTitle.value;
    missingFields.value = result.missingFields || [];
    filledSlots.value = result.filledSlots || {};
    if (result.documentId) documentId.value = result.documentId;
    if (result.documentTitle) documentTitle.value = result.documentTitle;
  }

  function updatePendingMessage(
    content: string,
    result: Partial<TemplateGenerationResult> & { deliverySummary?: string },
    thought?: ThoughtStep[],
    overrideStatus?: string,
  ) {
    const msgId = pendingMessageId.value;
    if (!msgId) return;
    const scenario = resolveScenario();
    const sessionId = boundSessionId.value || undefined;
    const prevMsg = aiStore.getMessages(scenario, sessionId).find((m) => m.id === msgId);
    const prevSr = prevMsg?.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
    const merged: Partial<TemplateGenerationResult> & { deliverySummary?: string } = {
      ...result,
      reviewChecklist: result.reviewChecklist ?? prevSr?.reviewChecklist,
      deliverySummary: result.deliverySummary ?? prevSr?.deliverySummary,
      intentSummary: result.intentSummary ?? prevSr?.intentSummary,
      outlineSummary: result.outlineSummary ?? prevSr?.outlineSummary,
      qualitySummary: result.qualitySummary ?? prevSr?.qualitySummary,
      pendingConfirmCount: result.pendingConfirmCount ?? prevSr?.pendingConfirmCount,
      processSteps: result.processSteps ?? prevSr?.processSteps,
      matchHits: result.matchHits ?? prevSr?.matchHits,
      referenceSummaries: result.referenceSummaries ?? prevSr?.referenceSummaries,
    };
    aiStore.updateMessage(
      scenario,
      msgId,
      {
        content,
        structuredResult: buildStructured(merged, overrideStatus) as unknown as Record<string, unknown>,
        ...(thought ? { thought: thoughtStepsToJson(thought) } : {}),
      },
      sessionId,
    );
  }

  function clearGeneratingListState() {
    liveListItemId.value = '';
    userSwitchedDuringGenerate.value = false;
  }

  function clearDeferredDocumentId() {
    deferredDocumentId.value = null;
  }

  function markUserSwitchedDuringGenerate() {
    if (livePreviewActive.value) {
      userSwitchedDuringGenerate.value = true;
    }
  }

  function clearLivePreview() {
    livePreviewActive.value = false;
    livePreviewHtml.value = '';
    livePreviewTitle.value = '';
    livePhaseMessage.value = '';
    liveWrittenCount.value = 0;
    liveTotal.value = 0;
    liveProcessSteps.value = [];
    clearGeneratingListState();
  }

  function startLivePreview(title?: string) {
    livePreviewActive.value = true;
    livePreviewHtml.value = '';
    livePreviewTitle.value = (title || templateTitle.value || '文档生成中').trim();
    livePhaseMessage.value = '';
    liveWrittenCount.value = 0;
    liveTotal.value = 0;
    liveProcessSteps.value = [];
    if (!liveListItemId.value) {
      liveListItemId.value = `generating:${Date.now()}`;
    }
    userSwitchedDuringGenerate.value = false;
    deferredDocumentId.value = null;
  }

  function applyLivePreviewFromProgress(data: Record<string, unknown>) {
    const detail = (data.detail && typeof data.detail === 'object'
      ? data.detail
      : {}) as Record<string, unknown>;
    const preview = String(data.previewHtml || detail.previewHtml || '').trim();
    if (preview) {
      livePreviewHtml.value = preview;
    }
    const phaseMsg = String(data.message || detail.message || '').trim();
    const currentSection = String(data.currentSection || detail.currentSection || '').trim();
    if (phaseMsg) {
      livePhaseMessage.value = phaseMsg;
    } else if (currentSection) {
      livePhaseMessage.value = currentSection;
    }
    const written = data.writtenCount ?? detail.writtenCount;
    const total = data.total ?? detail.total;
    if (typeof written === 'number' && Number.isFinite(written)) {
      liveWrittenCount.value = written;
    }
    if (typeof total === 'number' && Number.isFinite(total)) {
      liveTotal.value = total;
    }
    const processSteps = data.processSteps as TemplateGenerationProcessStep[] | undefined;
    if (processSteps?.length) {
      liveProcessSteps.value = processSteps;
    }
  }

  function endFlow() {
    active.value = false;
    sending.value = false;
    generationId.value = '';
    status.value = '';
    templateId.value = '';
    templateTitle.value = '';
    missingFields.value = [];
    filledSlots.value = {};
    documentId.value = '';
    documentTitle.value = '';
    pendingMessageId.value = null;
    abortController.value = null;
    clearLivePreview();
  }

  function cancelSending() {
    abortController.value?.abort();
    abortController.value = null;
    sending.value = false;
    clearLivePreview();
  }

  /** 放弃当前生成结果并重试：保留模版引用，仅清 generation 会话 */
  function resetForRetry() {
    cancelSending();
    generationId.value = '';
    status.value = '';
    pendingMessageId.value = null;
    missingFields.value = [];
    documentId.value = '';
    documentTitle.value = '';
    active.value = true;
  }

  async function sendUserMessage(
    text: string,
    opts?: {
      signal?: AbortSignal;
      onThought?: (steps: ThoughtStep[]) => void;
      attachments?: Array<{ name?: string; objectKey?: string; accessUrl?: string; type?: string }>;
    },
  ): Promise<{ aborted?: boolean; result?: TemplateGenerationResult }> {
    const normalized = text.trim() || '(使用模版生成)';
    if (sending.value) return {};

    const tpl = smartDocAi.templateAtToken;
    const continuing = canContinue.value;
    const templateRefId = (tpl?.refId || templateId.value || '').trim();
    if (!continuing && !templateRefId) {
      ElMessage.warning('请先通过模版库「使用」或 @模板 引用模版');
      return {};
    }

    resolveScenario();
    active.value = true;
    sending.value = true;
    if (!continuing) {
      startLivePreview(tpl?.name || templateTitle.value);
    } else if (!livePreviewActive.value) {
      startLivePreview(tpl?.name || templateTitle.value);
    } else {
      livePreviewTitle.value = (tpl?.name || templateTitle.value || livePreviewTitle.value).trim();
    }

    if (tpl?.refId) {
      templateId.value = tpl.refId;
      templateTitle.value = tpl.name || templateTitle.value;
    }

    const localAbort = new AbortController();
    abortController.value = localAbort;
    const onAbort = () => localAbort.abort();
    opts?.signal?.addEventListener('abort', onAbort);

    const atTokensPayload = (() => {
      const wid = String(smartDocAi.workspaceId || '').trim();
      // 业务项目工作台：确保 @项目 进入 searchProjectIds，避免仅落在 workspace=0
      if (wid && wid !== '0') {
        const hasProject = smartDocAi.atTokens.some((t) => {
          if (t.kind !== 'project') return false;
          const rid = String(t.refId || '').trim();
          return rid === wid || rid.startsWith(`${wid}:`);
        });
        if (!hasProject) {
          smartDocAi.addAtRef({
            kind: 'project',
            name: '当前项目',
            refId: wid,
            label: `@项目-${wid}`,
          });
        }
      }
      return smartDocAi.atTokens.map((t) => ({
        kind: t.kind,
        name: t.name,
        refId: t.refId,
        label: t.label,
      }));
    })();
    const skillIdsSnap = [...smartDocAi.selectedSkillIds];
    const attachmentsPayload = (opts?.attachments || [])
      .filter((a) => a?.objectKey && String(a.objectKey).trim())
      .map((a) => ({
        name: a.name,
        objectKey: String(a.objectKey).trim(),
        accessUrl: a.accessUrl,
        type: a.type,
      }));

    addMessage('user', normalized);
    const atTokensForChat = atTokensPayload.length
      ? atTokensPayload.map((t) => ({
          kind: t.kind,
          name: t.name,
          label: t.label,
          refId: t.refId,
        }))
      : undefined;
    if (atTokensForChat?.length) {
      const scenario = resolveScenario();
      const sessionId = boundSessionId.value || undefined;
      const list = aiStore.getMessages(scenario, sessionId);
      const lastUser = [...list].reverse().find((m) => m.role === 'user');
      if (lastUser) {
        aiStore.updateMessage(scenario, lastUser.id, { atTokens: atTokensForChat }, sessionId);
      }
    }
    // 发送后清掉输入区引用 chip，避免生成过程中残留；会话内 generationId / templateId 仍可续轮
    smartDocAi.clearAtTokens();

    let steps: ThoughtStep[] = templateGenerateThoughtInitial();
    opts?.onThought?.(steps);

    const projectName = (() => {
      const proj = atTokensPayload.find((t) => t.kind === 'project' || String(t.kind || '').includes('project'));
      return (proj?.name || proj?.label || '').trim().replace(/^@项目-?/, '') || null;
    })();
    const understanding = buildTemplateGenerateUnderstandingMessage({
      continuing,
      templateTitle: tpl?.name || templateTitle.value,
      projectName,
    });

    const progressMsg = addMessage(
      'assistant',
      understanding,
      buildStructured({
        status: 'generating',
        generationId: generationId.value || undefined,
        templateId: templateRefId || tpl?.refId || templateId.value,
        templateTitle: tpl?.name || templateTitle.value,
      }),
      thoughtStepsToJson(steps),
    );
    pendingMessageId.value = progressMsg.id;

    const onStreamEvent = (event: {
      type: 'progress' | 'done' | 'error';
      data: Record<string, unknown> | TemplateGenerationResult | { message?: string };
    }) => {
      if (event.type !== 'progress') return;
      const data = event.data as Record<string, unknown>;
      const thought = data.thought as { steps?: Array<{ key?: string; title?: string; text?: string; icon?: string }> } | undefined;
      const processSteps = data.processSteps as TemplateGenerateProgressStep[] | undefined;
      steps = mapTemplateGenerateProgressToThought(processSteps, thought?.steps);
      opts?.onThought?.(steps);
      const phaseMsg = String(data.message || '').trim();
      if (data.generationId) {
        generationId.value = String(data.generationId);
      }
      if (data.status) {
        status.value = String(data.status);
      }
      applyLivePreviewFromProgress(data);
      updatePendingMessage(
        phaseMsg || '正在生成文档…',
        {
          status: String(data.status || 'generating'),
          generationId: generationId.value || undefined,
          templateId: templateRefId || templateId.value,
          templateTitle: tpl?.name || templateTitle.value,
          processSteps: processSteps as TemplateGenerationResult['processSteps'],
        },
        steps,
        String(data.status || 'generating'),
      );
    };

    try {
      let result: TemplateGenerationResult;
      if (continuing && generationId.value) {
        result = await consumeTemplateGenerationMessageStream(
          smartDocAi.workspaceId || '0',
          generationId.value,
          {
            userMessage: normalized,
            atTokens: atTokensPayload,
            attachments: attachmentsPayload,
          },
          {
            signal: localAbort.signal,
            onEvent: onStreamEvent,
          },
        );
      } else {
        result = await consumeTemplateGenerationStartStream(
          smartDocAi.workspaceId || '0',
          {
            templateId: templateRefId,
            skillIds: skillIdsSnap,
            userMessage: normalized,
            sessionId: boundSessionId.value || undefined,
            atTokens: atTokensPayload,
            attachments: attachmentsPayload,
          },
          {
            signal: localAbort.signal,
            onEvent: onStreamEvent,
          },
        );
      }

      if (localAbort.signal.aborted) {
        updatePendingMessage('已终止本次模版生成。', { status: 'failed', errorMessage: '已终止' }, steps, 'failed');
        status.value = 'failed';
        clearLivePreview();
        return { aborted: true };
      }

      applyResult(result);
      steps = mapTemplateGenerateProgressToThought(
        result.processSteps as TemplateGenerateProgressStep[] | undefined,
        null,
      );
      if (result.status === 'completed') {
        const pending = result.missingFields || [];
        const deliverySummary = (result.assistantReply || '').trim()
          || '已生成文档草稿，正在写入工作台…';
        steps = [
          ...steps,
          {
            key: 'tpl_ready',
            icon: pending.length ? '❓' : '✅',
            title: pending.length ? '已生成（待完善）' : '已生成',
            text: pending.length
              ? `仍有 ${pending.length} 项建议核对；正在打开预览…`
              : '正在打开预览…',
          },
        ];
        opts?.onThought?.(steps);
        updatePendingMessage(
          deliverySummary,
          { ...result, deliverySummary },
          steps,
        );
        // 生成完成后自动创建并打开工作台文档
        livePhaseMessage.value = '正在打开预览…';
        const openedId = await confirmOpen(pendingMessageId.value || undefined, deliverySummary);
        if (openedId) {
          steps = [
            ...steps.filter((s) => s.key !== 'tpl_ready'),
            {
              key: 'tpl_opened',
              icon: '✅',
              title: '已打开预览',
              text: documentTitle.value
                ? `《${documentTitle.value}》已在右侧打开`
                : '文档已在右侧打开',
            },
          ];
          opts?.onThought?.(steps);
          const openedReply = appendOpenedHint(deliverySummary, documentTitle.value);
          updatePendingMessage(
            openedReply,
            {
              ...result,
              status: 'opened',
              documentId: openedId,
              documentTitle: documentTitle.value || undefined,
              generationId: generationId.value || undefined,
              templateId: templateId.value || undefined,
              templateTitle: templateTitle.value || undefined,
              deliverySummary,
              reviewChecklist: result.reviewChecklist,
            },
            steps,
            'opened',
          );
        }
        return { result: { ...result, documentId: openedId || result.documentId } };
      } else if (result.status === 'collecting') {
        // 正文 assistantReply 已是 Markdown，此处不再重复「待补充」列表
        steps = [
          ...steps,
          {
            key: 'tpl_ask',
            icon: '❓',
            title: '待补充',
            text: '请按下方说明补充后继续',
          },
        ];
      } else if (result.status === 'failed') {
        steps = [
          ...steps,
          {
            key: 'tpl_err',
            icon: '⚠️',
            title: '失败',
            text: result.errorMessage || result.assistantReply || '生成失败',
          },
        ];
      }

      opts?.onThought?.(steps);
      updatePendingMessage(
        result.assistantReply || (result.status === 'completed' ? '已生成文档草稿' : '请继续补充信息'),
        result,
        steps,
      );
      return { result };
    } catch (e) {
      if (isAbortError(e) || localAbort.signal.aborted) {
        updatePendingMessage('已终止本次模版生成。', { status: 'failed', errorMessage: '已终止' }, steps, 'failed');
        status.value = 'failed';
        active.value = true;
        clearLivePreview();
        return { aborted: true };
      }
      if (generationId.value && isTemplateGenerationNetworkError(e, formatTemplateGenerationError(e))) {
        const polled = await pollTemplateGeneration(
          smartDocAi.workspaceId || '0',
          generationId.value,
          localAbort.signal,
        );
        if (polled) {
          applyResult(polled);
          if (polled.status === 'completed') {
            const deliverySummary = (polled.assistantReply || '').trim()
              || '已生成文档草稿';
            const openedId = await confirmOpen(pendingMessageId.value || undefined, deliverySummary);
            if (openedId) {
              updatePendingMessage(
                appendOpenedHint(deliverySummary, documentTitle.value || polled.documentTitle),
                {
                  ...polled,
                  status: 'opened',
                  documentId: openedId,
                  deliverySummary,
                },
                steps,
                'opened',
              );
            }
            return { result: { ...polled, documentId: openedId || polled.documentId } };
          }
          if (polled.status === 'failed') {
            const msg = polled.errorMessage || polled.assistantReply || '生成失败';
            status.value = 'failed';
            updatePendingMessage(msg, { status: 'failed', errorMessage: msg }, steps, 'failed');
            return { result: polled };
          }
        }
      }
      const msg = formatTemplateGenerationError(e);
      status.value = 'failed';
      active.value = true;
      clearLivePreview();
      steps = [
        ...steps,
        { key: 'tpl_err', icon: '⚠️', title: '失败', text: msg },
      ];
      updatePendingMessage(msg, { status: 'failed', errorMessage: msg }, steps, 'failed');
      if (!isTemplateGenerationNetworkError(e, msg)) {
        ElMessage.error(msg);
      } else {
        ElMessage.warning(msg);
      }
      return {};
    } finally {
      opts?.signal?.removeEventListener('abort', onAbort);
      if (abortController.value === localAbort) {
        abortController.value = null;
      }
      sending.value = false;
    }
  }

  async function confirmOpen(messageId?: string, deliverySummary?: string): Promise<string | null> {
    if (!generationId.value) {
      ElMessage.warning('没有可确认的生成结果');
      return null;
    }
    if (status.value !== 'completed' && status.value !== 'opening') {
      ElMessage.warning('生成尚未完成');
      return null;
    }
    sending.value = true;
    status.value = 'opening';
    livePhaseMessage.value = '正在打开预览…';
    if (messageId) pendingMessageId.value = messageId;
    const preservedSummary = (deliverySummary || '').trim();
    // 打开过程中保留交付摘要，仅追加短暂状态；禁止用「已创建」覆盖正文
    updatePendingMessage(
      preservedSummary
        ? `${preservedSummary}\n\n正在打开预览…`
        : '正在打开预览…',
      {
        status: 'opening',
        deliverySummary: preservedSummary || undefined,
      },
      undefined,
      'opening',
    );
    try {
      const result = await confirmTemplateGeneration(
        smartDocAi.workspaceId || '0',
        generationId.value,
      );
      applyResult(result);
      status.value = 'opened';
      const docId = (result.documentId || '').trim();
      const shouldAutoOpen = !userSwitchedDuringGenerate.value;
      // 先清掉 generating:… 占位，避免工作台 snapshot/详情把占位 id 当 Long
      clearLivePreview();
      if (docId) {
        if (shouldAutoOpen) {
          smartDocAi.markDocumentApplied(docId, smartDocAi.workspaceId || null);
        } else {
          deferredDocumentId.value = docId;
        }
        // 生成完成后移除模版引用，避免重复开新会话误触发
        const tplToken = smartDocAi.templateAtToken;
        if (tplToken) smartDocAi.removeAtToken(tplToken.id);
      }
      const summary = preservedSummary || (result.assistantReply || '').trim();
      const finalReply = appendOpenedHint(summary, result.documentTitle || documentTitle.value);
      updatePendingMessage(
        finalReply,
        {
          ...result,
          status: 'opened',
          deliverySummary: summary || undefined,
          reviewChecklist: result.reviewChecklist,
        },
        undefined,
        'opened',
      );
      active.value = false;
      if (shouldAutoOpen) {
        ElMessage.success('文档已打开');
      }
      return docId || null;
    } catch (e) {
      status.value = 'completed';
      const msg = formatApiErrorMessage(e);
      updatePendingMessage(
        preservedSummary ? `${preservedSummary}\n\n打开失败：${msg}` : msg,
        {
          status: 'completed',
          errorMessage: msg,
          deliverySummary: preservedSummary || undefined,
        },
        undefined,
        'completed',
      );
      ElMessage.error(msg);
      return null;
    } finally {
      sending.value = false;
    }
  }

  return {
    active,
    sending,
    generationId,
    status,
    templateId,
    templateTitle,
    missingFields,
    filledSlots,
    documentId,
    documentTitle,
    pendingMessageId,
    livePreviewActive,
    livePreviewHtml,
    livePreviewTitle,
    livePhaseMessage,
    liveWrittenCount,
    liveTotal,
    liveProcessSteps,
    liveListItemId,
    userSwitchedDuringGenerate,
    deferredDocumentId,
    isCollecting,
    canContinue,
    bindPanelContext,
    sendUserMessage,
    confirmOpen,
    cancelSending,
    resetForRetry,
    endFlow,
    markUserSwitchedDuringGenerate,
    clearDeferredDocumentId,
  };
});
